<template>
    <div id="hive-chart-widget-component">
        <WidgetWrapperComponent :title="'Evolution ruche'" id-widget="hive-chart-widget">
            <template v-slot:default>
                <DateRangePickerComponent
                    :date_range="date_range"
                    :loading="loading_hive_chart_data"
                    class="mb-4"
                    v-on:update="updateRangeDate"
                />

                <div v-if="chart_options?.series && chart_options.series.length > 1" class="d-flex flex-wrap gap-2 mb-4">
                    <template v-for="(serie, index) in chart_options.series">
                        <v-checkbox
                            :key="index"
                            v-model="is_chart_series_display[index]"
                            :color="serie.color"
                            :label="`${serie.name} ${serie.tooltip.valueSuffix ? `(${serie.tooltip.valueSuffix.trim()})` : ''}`"
                            class="mt-0 grow"
                            hide-details
                            @click="fillChartSeries()"
                        />
                    </template>
                </div>

                <highcharts v-if="chart_options.series[0].data !== null" :options="chart_options" />
                <NoDataSvgComponent v-else />
            </template>
        </WidgetWrapperComponent>
    </div>
</template>

<script>
import WidgetWrapperComponent from '@/components/global/wrapper/WidgetWrapperComponent.vue'
import DateRangePickerComponent from '@/components/global/picker/DateRangePickerComponent.vue'
import moment from 'moment/moment'
import DeviceRepository from '@/repositories/DeviceRepository'
import NoDataSvgComponent from '@/components/global/svg/NoDataSvgComponent.vue'

export default {
    name: 'HiveChartWidgetComponent',
    components: { NoDataSvgComponent, DateRangePickerComponent, WidgetWrapperComponent },
    props: {
        idDevice: {
            type: Number,
            required: true,
        },
    },
    data() {
        return {
            loading_hive_chart_data: false,

            date_range: {
                startDate: this.moment().clone().startOf('day').subtract(7, 'day').toDate(),
                endDate: this.moment().toDate(),
            },
            is_chart_series_display: [true, true, true, true],
            chart_series_data: [[], [], [], []],

            chart_options: {
                lang: {
                    noData: this.$t('error_display.no_data'),
                },
                time: {
                    timezone: 'Europe/Paris',
                },
                title: {
                    text: null,
                },
                exporting: {
                    accessibility: {
                        enabled: true,
                    },
                    menuItemDefinitions: {
                        downloadPNG: {
                            text: 'Télécharger au format PNG',
                        },
                        downloadPDF: {
                            text: 'Télécharger au format PDF',
                        },
                    },
                    buttons: {
                        contextButton: {
                            menuItems: ['downloadPNG', 'downloadPDF'],
                        },
                    },
                    enabled: true,
                    filename: 'text_filename',
                },
                legend: {
                    enabled: false,
                },
                xAxis: {
                    type: 'datetime',
                    categories: [],
                    labels: {
                        formatter: function () {
                            return moment(this.value).format('DD MMM')
                        },
                    },
                },
                yAxis: [
                    {
                        // Axe gauche
                        title: {
                            text: 'Poids (kg)',
                        },
                        labels: {
                            format: '{value} kg',
                            style: {
                                color: '#7cb5ec',
                            },
                        },
                    },
                    {
                        // Axe droit
                        title: {
                            text: 'Température (°C)',
                        },
                        min: -15,
                        max: 40,
                        labels: {
                            format: '{value} °C',
                            style: {
                                color: '#434348',
                            },
                        },
                        opposite: true, // Positionné à droite
                    },
                    {
                        // Axe droit
                        title: {
                            text: 'Humidité (%)',
                        },
                        min: 0,
                        max: 100,
                        labels: {
                            format: '{value} %',
                            style: {
                                color: '#434348',
                            },
                        },
                        opposite: true, // Positionné à droite
                    },
                ],
                series: [
                    {
                        name: 'Poids',
                        type: 'spline',
                        data: [],
                        yAxis: 0,
                        tooltip: {
                            valueSuffix: ' kg',
                        },
                        color: '#F69616',
                    },
                    {
                        name: 'Gain',
                        type: 'spline',
                        data: [],
                        yAxis: 0,
                        tooltip: {
                            valueSuffix: ' L',
                        },
                        color: '#65B265',
                    },
                    {
                        name: 'Température',
                        type: 'spline',
                        data: [],
                        yAxis: 1,
                        tooltip: {
                            valueSuffix: ' °C',
                        },
                        color: '#90CAF9',
                    },
                    {
                        name: 'Humidité',
                        type: 'spline',
                        data: [],
                        yAxis: 2,
                        tooltip: {
                            valueSuffix: ' %',
                        },
                        color: '#CFD8DC',
                    },
                ],
            },
        }
    },
    created() {
        this.getHiveChartData()
    },
    methods: {
        getHiveChartData() {
            this.loading_hive_chart_data = false

            DeviceRepository.getHiveChartData(
                this.$props.idDevice,
                this.setDateForRequest(this.date_range.startDate),
                this.setDateForRequest(this.date_range.endDate)
            )
                .then((response) => {
                    response.data.data.forEach((data) => {
                        this.chart_options.xAxis.categories.push(data.date)
                        this.chart_series_data[0].push(parseFloat(data.weight))
                        this.chart_series_data[1].push(parseFloat(data.win))
                        this.chart_series_data[2].push(parseFloat(data.temperature))
                        this.chart_series_data[3].push(parseFloat(data.humidity))
                    })
                    this.fillChartSeries()
                })
                .catch((error) => {
                    this.manageError(error)
                })
                .finally(() => {
                    this.loading_hive_chart_data = false
                })
        },

        fillChartSeries() {
            for (let index = 0; index < 4; index++) {
                this.chart_options.series[index].data = []
                if (this.is_chart_series_display[index]) {
                    this.chart_options.series[index].data = this.chart_series_data[index]
                }
            }
        },

        updateRangeDate(date_range) {
            this.date_range = date_range
            this.getHiveChartData()
        },
    },
}
</script>
